import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle, RotateCcw, ChevronRight, Info, Loader2 } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { api } from "@/lib/api-client";
import { Button } from "@/components/ui/Button";
import { StatusBadge } from "@/components/common/StatusBadge";
import { PageSkeleton } from "@/components/common/LoadingSkeleton";
import { Textarea } from "@/components/ui/Textarea";
import { toast } from "@/components/ui/Toast";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";
import { formatDate } from "@/lib/format";
import {
  parseChoices,
  parseSubFields,
  type QuestionType,
  type QuestionOptions,
} from "@/components/assessments/QuestionCard";
import type { VendorAssessmentStatus } from "@/types";

// ─────────────────────────────────────────────
// VendorAssessmentReview  (WP11 — L-030)
//
// Screen — /vendors/:id/assessments/:aId
// A submitted vendor assessment (status UNDER_REVIEW) is scored per question
// (scores autosave), then Approved (→ COMPLETED) or sent back with a required
// rationale via Request Revision (→ IN_PROGRESS). Once COMPLETED it is read-only.
// Design: §8.5 Vendor Assessment Review
// ─────────────────────────────────────────────

interface QuestionRecord {
  id: string;
  code: string;
  text: string;
  helpText?: string;
  type: QuestionType;
  options: QuestionOptions | null;
  sortOrder: number;
}

interface PostureRecord {
  id: string;
  code: string;
  name: string;
  sortOrder: number;
  questions: QuestionRecord[];
}

interface DomainRecord {
  id: string;
  code: string;
  name: string;
  sortOrder: number;
  postures: PostureRecord[];
}

interface ResponseRecord {
  id: string;
  questionId: string;
  value: unknown;
  comment: string | null;
}

interface ReviewScoreRecord {
  questionId: string;
  score: number;
  note?: string;
}

interface VendorAssessmentReviewResponse {
  data: {
    id: string;
    vendorId: string;
    vendorName: string;
    schemaName: string;
    status: VendorAssessmentStatus;
    sentAt: string | null;
    completedAt: string | null;
    schemaVersion?: {
      domains?: DomainRecord[];
    };
    responses?: ResponseRecord[];
    reviewScores?: ReviewScoreRecord[];
  };
}

// Local review score state per question
type ReviewScores = Record<string, { score: number; note: string }>;

function titleCase(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

// Render a vendor's raw answer value as human-readable option label(s) — S-106.
// Falls back gracefully so a MULTI_SUB_FIELD object never renders "[object Object]"
// and choice keys never leak (e.g. "fully_implemented" → "Fully Implemented").
function renderVendorAnswer(
  type: QuestionType,
  options: QuestionOptions | null,
  value: unknown,
  t: (key: string) => string,
): string {
  if (value === null || value === undefined || value === "") return t("vendor.review.noAnswer");

  if (type === "WEIGHTED_MULTIPLE_CHOICE" || type === "WEIGHTED_YES_NO") {
    const key = String(value);
    const choice = parseChoices(options).find((c) => c.value === key);
    return choice ? choice.label : titleCase(key);
  }

  if (type === "MULTI_SUB_FIELD" && typeof value === "object") {
    const subFields = parseSubFields(options);
    const parts: string[] = [];
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (v === "" || v === null || v === undefined) continue;
      const sf = subFields.find((s) => s.name === k);
      const optLabel =
        sf?.options.find((o) => o.value === String(v))?.label ?? titleCase(String(v));
      parts.push(`${sf?.name ?? k}: ${optLabel}`);
    }
    return parts.length ? parts.join("  ·  ") : t("vendor.review.noAnswer");
  }

  return String(value);
}

function ScoreButton({
  score,
  active,
  onClick,
}: {
  score: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex h-8 w-8 items-center justify-center rounded-control border text-[13px] font-semibold transition-colors",
        active
          ? "border-accent bg-accent-bg text-accent-text"
          : "border-border-default bg-bg-input text-text-muted hover:border-accent hover:text-accent"
      )}
    >
      {score}
    </button>
  );
}

export function VendorAssessmentReview() {
  const { id, aId } = useParams<{ id: string; aId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t, language } = useTranslation();

  const [activeDomainId, setActiveDomainId] = useState<string | null>(null);
  const [scores, setScores] = useState<ReviewScores>({});
  const [saving, setSaving] = useState(false);
  const [revisionOpen, setRevisionOpen] = useState(false);
  const [revisionText, setRevisionText] = useState("");

  const { data, isLoading } = useQuery<VendorAssessmentReviewResponse>({
    queryKey: ["vendor-assessment-review", aId],
    queryFn: () => api.get(`/vendor-assessments/${aId}`),
    enabled: Boolean(aId),
  });

  const status = data?.data?.status;
  const isUnderReview = status === "UNDER_REVIEW";
  const isApproved = status === "COMPLETED";

  // Hydrate local score state from persisted reviewScores once, so autosaved work
  // is shown on reload and Approve submits the full set (S-099 / L-035).
  const hydratedRef = useRef<string | null>(null);
  useEffect(() => {
    const d = data?.data;
    if (!d || hydratedRef.current === d.id) return;
    hydratedRef.current = d.id;
    const seeded: ReviewScores = {};
    for (const s of d.reviewScores ?? []) {
      seeded[s.questionId] = { score: s.score, note: s.note ?? "" };
    }
    setScores(seeded);
  }, [data?.data]);

  // Per-question autosave. Serializes writes so rapid edits never race, and
  // debounces note typing. A score click persists immediately. WP11 (S-099/L-035).
  const inFlight = useRef<Promise<unknown>>(Promise.resolve());
  const noteTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const persist = useCallback(
    (questionId: string, score: number, note: string) => {
      if (!isUnderReview || !score) return; // backend requires a 1-5 score
      setSaving(true);
      inFlight.current = inFlight.current
        .catch(() => {})
        .then(() =>
          api.post(`/vendor-assessments/${aId}/review-scores`, {
            questionId,
            score,
            note: note ? note : undefined,
          })
        )
        .then(() => setSaving(false))
        .catch((err: Error) => {
          setSaving(false);
          toast.error(t("vendor.review.saveScoreFailed", { message: err.message }));
        });
    },
    [aId, isUnderReview, t]
  );

  const approveMutation = useMutation({
    mutationFn: () =>
      api.post(`/vendor-assessments/${aId}/complete`, {
        reviewScores: Object.entries(scores)
          .filter(([, s]) => s.score > 0)
          .map(([questionId, s]) => ({
            questionId,
            score: s.score,
            note: s.note || undefined,
          })),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendor-assessment-review", aId] });
      queryClient.invalidateQueries({ queryKey: ["vendor-assessments", id] });
      // The profile KPIs (compliance rate / risk score) come from the vendor
      // detail query — refresh it too or they show stale values until reload.
      queryClient.invalidateQueries({ queryKey: ["vendor", id] });
      toast.success(t("vendor.review.approvedToast"));
      navigate(`/vendors/${id}`);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const revisionMutation = useMutation({
    mutationFn: () =>
      api.post(`/vendor-assessments/${aId}/request-revision`, { comment: revisionText.trim() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendor-assessment-review", aId] });
      queryClient.invalidateQueries({ queryKey: ["vendor-assessments", id] });
      queryClient.invalidateQueries({ queryKey: ["vendor", id] });
      toast.success(t("vendor.review.revisionRequestedToast"));
      setRevisionOpen(false);
      setRevisionText("");
      navigate(`/vendors/${id}`);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (isLoading) return <PageSkeleton />;

  const assessment = data?.data;
  if (!assessment) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-text-muted">
        {t("assessment.notFound")}
      </div>
    );
  }

  const domains = assessment.schemaVersion?.domains ?? [];
  const responses = assessment.responses ?? [];
  const responseMap = new Map(responses.map((r) => [r.questionId, r]));

  const activeDomain = activeDomainId
    ? domains.find((d) => d.id === activeDomainId) ?? domains[0]
    : domains[0];

  function setScore(questionId: string, score: number) {
    const note = scores[questionId]?.note ?? "";
    setScores((prev) => ({
      ...prev,
      [questionId]: { score, note: prev[questionId]?.note ?? "" },
    }));
    persist(questionId, score, note);
  }

  function setNote(questionId: string, note: string) {
    const score = scores[questionId]?.score ?? 0;
    setScores((prev) => ({
      ...prev,
      [questionId]: { score: prev[questionId]?.score ?? 0, note },
    }));
    const timers = noteTimers.current;
    const existing = timers.get(questionId);
    if (existing) clearTimeout(existing);
    timers.set(
      questionId,
      setTimeout(() => persist(questionId, score, note), 800)
    );
  }

  return (
    <>
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-caption text-text-muted">
        <Link to="/vendors" className="hover:text-text-primary">{t("nav.vendors")}</Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <Link to={`/vendors/${id}`} className="hover:text-text-primary">{assessment.vendorName}</Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <span className="text-text-secondary">{assessment.schemaName}</span>
      </nav>

      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/vendors/${id}`)}
            className="flex items-center gap-2 text-body text-text-muted hover:text-text-primary"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-[20px] font-semibold text-text-primary">
                {assessment.vendorName} - {assessment.schemaName}
              </h1>
              <StatusBadge type="vendorAssessment" status={assessment.status} />
            </div>
            {assessment.sentAt && (
              <p className="mt-0.5 text-caption text-text-muted">
                {t("vendor.review.sentDate", { date: formatDate(assessment.sentAt, language) })}
                {assessment.completedAt && ` · ${t("vendor.review.approvedDate", { date: formatDate(assessment.completedAt, language) })}`}
              </p>
            )}
          </div>
        </div>

        {isUnderReview && (
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1.5 text-caption text-text-muted">
              {saving ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("common.saving")}
                </>
              ) : (
                t("vendor.review.autosaveHint")
              )}
            </span>
            <Button variant="outline" onClick={() => setRevisionOpen(true)}>
              <RotateCcw className="mr-2 h-4 w-4" />
              {t("assessment.requestRevision")}
            </Button>
            <Button
              onClick={() => approveMutation.mutate()}
              isLoading={approveMutation.isPending}
            >
              <CheckCircle className="mr-2 h-4 w-4" />
              {t("common.approve")}
            </Button>
          </div>
        )}
      </div>

      {/* Pre-submission notice: reviewer can't act until the vendor submits */}
      {!isUnderReview && !isApproved && (
        <div className="flex items-start gap-3 rounded-card border border-border-default bg-hover px-4 py-3">
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-text-muted" />
          <p className="text-body text-text-secondary">
            {t("vendor.review.preSubmitNotice")}
          </p>
        </div>
      )}

      {/* Content: sidebar + questions */}
      <div className="flex gap-6">
        {/* Section navigation sidebar */}
        <div className="w-[260px] flex-shrink-0">
          <div className="rounded-card border border-border-default bg-card p-3">
            <p className="mb-2 px-2 text-caption font-medium uppercase tracking-wider text-text-muted">
              {t("vendorPortal.sections")}
            </p>
            <nav className="flex flex-col gap-0.5">
              {domains.map((domain) => {
                const isActive = (activeDomain?.id ?? "") === domain.id;
                const totalQ = domain.postures.reduce((sum, p) => sum + p.questions.length, 0);
                const reviewedQ = domain.postures.reduce(
                  (sum, p) => sum + p.questions.filter((q) => (scores[q.id]?.score ?? 0) > 0).length,
                  0
                );
                return (
                  <button
                    key={domain.id}
                    type="button"
                    onClick={() => setActiveDomainId(domain.id)}
                    className={cn(
                      "flex w-full items-center justify-between rounded-control px-2 py-2 text-left text-body transition-colors",
                      isActive
                        ? "bg-accent-bg text-accent-text"
                        : "text-text-secondary hover:bg-hover hover:text-text-primary"
                    )}
                  >
                    <span>{domain.name}</span>
                    <span className="text-caption text-text-muted">{reviewedQ}/{totalQ}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Question cards */}
        <div className="flex-1 min-w-0">
          {activeDomain ? (
            <div className="flex flex-col gap-6">
              <h2 className="text-[18px] font-semibold text-text-primary">{activeDomain.name}</h2>
              {activeDomain.postures.map((posture) => (
                <div key={posture.id}>
                  <h3 className="mb-3 text-[14px] font-semibold uppercase tracking-wider text-text-muted">
                    {posture.code} · {posture.name}
                  </h3>
                  <div className="flex flex-col gap-3">
                    {posture.questions.map((question) => {
                      const response = responseMap.get(question.id);
                      const qScore = scores[question.id];
                      return (
                        <div
                          key={question.id}
                          className="rounded-card border border-border-default bg-card p-4"
                        >
                          {/* Question code + text */}
                          <div className="mb-3 flex items-start gap-3">
                            <span className="mt-0.5 flex-shrink-0 font-mono text-caption text-text-muted">
                              {question.code}
                            </span>
                            <p className="text-body text-text-primary">{question.text}</p>
                          </div>

                          {/* Vendor response — rendered as option labels (S-106) */}
                          <div className="mb-3 rounded-control border border-border-subtle bg-hover px-3 py-2">
                            <p className="text-caption text-text-muted mb-1">{t("vendor.review.vendorResponse")}</p>
                            <p className="text-body text-text-secondary">
                              {renderVendorAnswer(question.type, question.options, response?.value, t)}
                            </p>
                            {response?.comment && (
                              <p className="mt-1 text-caption text-text-muted italic">
                                "{response.comment}"
                              </p>
                            )}
                          </div>

                          {/* Reviewer controls — editable while UNDER_REVIEW */}
                          {isUnderReview && (
                            <div className="flex flex-col gap-2">
                              <div className="flex items-center gap-2">
                                <span className="text-caption text-text-muted">{t("vendor.review.scoreLabel")}</span>
                                {[1, 2, 3, 4, 5].map((s) => (
                                  <ScoreButton
                                    key={s}
                                    score={s}
                                    active={qScore?.score === s}
                                    onClick={() => setScore(question.id, s)}
                                  />
                                ))}
                              </div>
                              <Textarea
                                placeholder={t("vendor.review.notePlaceholder")}
                                value={qScore?.note ?? ""}
                                onChange={(e) => setNote(question.id, e.target.value)}
                                className="min-h-[60px] text-[13px]"
                              />
                            </div>
                          )}

                          {/* Read-only score once approved */}
                          {isApproved && (
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-2">
                                <span className="text-caption text-text-muted">{t("vendor.review.scoreLabel")}</span>
                                {qScore?.score ? (
                                  <span className="font-mono text-accent-light">{qScore.score}/5</span>
                                ) : (
                                  <span className="text-caption text-text-muted">-</span>
                                )}
                              </div>
                              {qScore?.note && (
                                <p className="text-caption text-text-muted italic">"{qScore.note}"</p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center py-12 text-text-muted">
              {t("vendor.review.noSections")}
            </div>
          )}
        </div>
      </div>

      {/* Request Revision dialog — rationale required (L-036) */}
      <DialogPrimitive.Root
        open={revisionOpen}
        onOpenChange={(o) => {
          if (!o) {
            setRevisionOpen(false);
            setRevisionText("");
          }
        }}
      >
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay
            className="fixed inset-0 z-50 animate-fade-in"
            style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
          />
          <DialogPrimitive.Content
            className={cn(
              "fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2",
              "animate-fade-in rounded-card border border-border-default bg-card p-6 shadow-xl",
              "focus:outline-none"
            )}
          >
            <DialogPrimitive.Title className="mb-1.5 text-[18px] font-semibold text-text-primary">
              {t("assessment.requestRevision")}
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="mb-4 text-body text-text-secondary">
              {t("vendor.review.revisionDesc")}
            </DialogPrimitive.Description>
            <Textarea
              value={revisionText}
              onChange={(e) => setRevisionText(e.target.value)}
              placeholder={t("vendor.review.revisionPlaceholder")}
              className="min-h-[100px]"
            />
            <div className="mt-5 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setRevisionOpen(false);
                  setRevisionText("");
                }}
                disabled={revisionMutation.isPending}
              >
                {t("common.cancel")}
              </Button>
              <Button
                onClick={() => revisionMutation.mutate()}
                isLoading={revisionMutation.isPending}
                disabled={revisionText.trim().length < 3}
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                {t("vendor.review.sendBack")}
              </Button>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </>
  );
}
